import Box from "@/elements/Box";
import NumberInput from "@/elements/Input/NumberInput";
import {
  FormHelperText,
  Grid,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import Card from "@mui/material/Card";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RegistroDto, UpdateRegistroDto } from "@/api";
import { isNil, keyBy } from "lodash";

export type Registri_GFL_MONITORING_EM = {
  measEcTh1?: number;
  measEcTh2?: number;
  measBxTh1?: number;
  measBxTh2?: number;
};

export function registriToForm_GFL_MONITORING_EM(_registri: RegistroDto[]): {
  [r in keyof Registri_GFL_MONITORING_EM]: any;
} {
  const registri = keyBy(_registri, "registro");

  return {
    measEcTh1: isNil(registri["measEcTh1"])
      ? undefined
      : parseFloat(registri["measEcTh1"].value),
    measEcTh2: isNil(registri["measEcTh2"])
      ? undefined
      : parseFloat(registri["measEcTh2"].value),
    measBxTh1: isNil(registri["measBxTh1"])
      ? undefined
      : parseFloat(registri["measBxTh1"].value),
    measBxTh2: isNil(registri["measBxTh2"])
      ? undefined
      : parseFloat(registri["measBxTh2"].value),
  };
}

export function formToRegistri_GFL_MONITORING_EM(
  form: Registri_GFL_MONITORING_EM
): UpdateRegistroDto[] {
  const topicPrefix = "var";
  const _values: {
    [k in keyof Registri_GFL_MONITORING_EM]?: UpdateRegistroDto;
  } = {
    measEcTh1: isNil(form.measEcTh1)
      ? undefined
      : {
          registro: "measEcTh1",
          topicPrefix,
          value: form.measEcTh1,
        },
    measEcTh2: isNil(form.measEcTh2)
      ? undefined
      : {
          registro: "measEcTh2",
          topicPrefix,
          value: form.measEcTh2,
        },
    measBxTh1: isNil(form.measBxTh1)
      ? undefined
      : {
          registro: "measBxTh1",
          topicPrefix,
          value: form.measBxTh1,
        },
    measBxTh2: isNil(form.measBxTh2)
      ? undefined
      : {
          registro: "measBxTh2",
          topicPrefix,
          value: form.measBxTh2,
        },
  };
  return Object.values(_values);
}

export function EditorRegistri_GFL_MONITORING_EM({
  control,
  errors,
  register,
  getValues,
  readOnly = false,
}: {
  control: Control<Registri_GFL_MONITORING_EM>;
  errors: FieldErrors<Registri_GFL_MONITORING_EM>;
  register: UseFormRegister<Registri_GFL_MONITORING_EM>;
  getValues: UseFormGetValues<Registri_GFL_MONITORING_EM>;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Card>
        <Box p={2} flex={1}>
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography
              variant="h5"
              color="text"
              fontWeight="medium"
              gutterBottom
            >
              {t("gflMonitoringEm.sezioni.conducibilita")}
            </Typography>

            <Grid container rowSpacing={1} columnSpacing={2}>
              <Grid item sm={12} md={6}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                  >
                    {t("gflMonitoringEm.campi.soglia1")}
                  </Typography>
                  <Controller
                    control={control}
                    name="measEcTh1"
                    rules={{
                      min: {
                        value: 0,
                        message: t("gflMonitoringEm.errori.intervallo", {
                          min: 0,
                          max: 2000,
                        }),
                      },
                      max: {
                        value: 2000,
                        message: t("gflMonitoringEm.errori.intervallo", {
                          min: 0,
                          max: 2000,
                        }),
                      },
                      validate: (measEcTh1: number | undefined) => {
                        const { measEcTh2 } = getValues();
                        if (measEcTh1 && measEcTh2 && measEcTh1 > measEcTh2) {
                          return t("gflMonitoringEm.errori.soglia1SuperaSoglia2");
                        }
                        return true;
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 100 }}
                          endAdornment={
                            <InputAdornment position="end">{t("unita.msCm")}</InputAdornment>
                          }
                          numericInputProps={{ decimalScale: 1 }}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
              <Grid item sm={12} md={6}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                  >
                    {t("gflMonitoringEm.campi.soglia2")}
                  </Typography>
                  <Controller
                    control={control}
                    name="measEcTh2"
                    rules={{
                      min: {
                        value: 0,
                        message: t("gflMonitoringEm.errori.intervallo", {
                          min: 0,
                          max: 2000,
                        }),
                      },
                      max: {
                        value: 2000,
                        message: t("gflMonitoringEm.errori.intervallo", {
                          min: 0,
                          max: 2000,
                        }),
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 100 }}
                          endAdornment={
                            <InputAdornment position="end">{t("unita.msCm")}</InputAdornment>
                          }
                          numericInputProps={{
                            decimalScale: 1,
                            min: 0,
                            max: 2000,
                          }}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>

      <Card>
        <Box p={2} flex={1}>
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography
              variant="h5"
              color="text"
              fontWeight="medium"
              gutterBottom
            >
              {t("gflMonitoringEm.sezioni.concentrazione")}
            </Typography>

            <Grid container rowSpacing={1} columnSpacing={2}>
              <Grid item sm={12} md={6}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                  >
                    {t("gflMonitoringEm.campi.soglia1")}
                  </Typography>
                  <Controller
                    control={control}
                    name="measBxTh1"
                    rules={{
                      min: {
                        value: 0,
                        message: t("gflMonitoringEm.errori.intervallo", {
                          min: 0,
                          max: 30,
                        }),
                      },
                      max: {
                        value: 30,
                        message: t("gflMonitoringEm.errori.intervallo", {
                          min: 0,
                          max: 30,
                        }),
                      },
                      validate: (measBxTh1: number | undefined) => {
                        const { measBxTh2 } = getValues();
                        if (measBxTh1 && measBxTh2 && measBxTh1 > measBxTh2) {
                          return t("gflMonitoringEm.errori.soglia1SuperaSoglia2");
                        }
                        return true;
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 100 }}
                          endAdornment={
                            <InputAdornment position="end">{t("unita.percento")}</InputAdornment>
                          }
                          numericInputProps={{ decimalScale: 1 }}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
              <Grid item sm={12} md={6}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    mr={2}
                  >
                    {t("gflMonitoringEm.campi.soglia2")}
                  </Typography>
                  <Controller
                    control={control}
                    name="measBxTh2"
                    rules={{
                      min: {
                        value: 0,
                        message: t("gflMonitoringEm.errori.intervallo", {
                          min: 0,
                          max: 30,
                        }),
                      },
                      max: {
                        value: 30,
                        message: t("gflMonitoringEm.errori.intervallo", {
                          min: 0,
                          max: 30,
                        }),
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <Stack direction="column" alignItems="end">
                        <NumberInput
                          {...field}
                          sx={{ maxWidth: 100 }}
                          endAdornment={
                            <InputAdornment position="end">{t("unita.percento")}</InputAdornment>
                          }
                          numericInputProps={{
                            decimalScale: 1,
                            min: 0,
                            max: 30,
                          }}
                        />
                        {error && (
                          <FormHelperText error>
                            {error?.message}
                          </FormHelperText>
                        )}
                      </Stack>
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}
